import { Directive, OnDestroy } from '@angular/core';
import { Exercicio, FuncaoService, GlobalService, Login, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnexoLRFService } from '../service/anexo-lrf.service';

@Directive()
export class ReceitaCorrenteLiquidaLRF implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();
  public valorRCL = 0;

  constructor(
    protected anexoServico: AnexoLRFService,
    protected mes: number,
    protected exercicio: Exercicio) {
    this.funcaoService = new FuncaoService();
    this.globalService = new GlobalService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async obterReceitaCorrenteLiquida(orgaos: number[]) {
    const result = await this.anexoServico.obterRCL(this.mes, this.exercicio.id, orgaos).toPromise();
    return this.rclConteudo(result);
  }

  public montarRelatorio(orgaos: number[]) {
    const parametros: {} = {};
    if (this.mes) {
      parametros['mes'] = this.mes;
    }
    if (this.exercicio) {
      parametros['ano'] = this.exercicio.id;
    }
    this.anexoServico.obterRCL(this.mes, this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        parametros['orgaos'] = orgaos.join();
        Relatorio.imprimirPersonalizado('DEMONSTRATIVO DE APURAÇÃO  DA RECEITA CORRENTE LÍQUIDA - R.C.L. - Referência: ' +
          this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano
          , this.login.usuario.nome,
          this.login.orgao.nome, this.login.brasao,
          this.rclConteudo(dados),
          'landscape', 'DEMONSTRATIVO DE APURAÇÃO  DA RECEITA CORRENTE LÍQUIDA - R.C.L.',
          {
            linhas: {
              hLineWidth(i, node) {
                return 1;
              },
              vLineWidth(i) {
                return 1;
              },
              hLineColor(i) {
                return 'black';
              },
              paddingLeft(i) {
                return 3;
              },
              paddingRight(i, node) {
                return 3;
              }
            }
          });
      });
  }

  private rclConteudo(dados: any[]): {}[] {
    const registros: {}[] = [[{
      text: 'ESPECIFICAÇÃO',
      rowSpan: 2, alignment: 'center', bold: true
    },
    {
      text: 'EVOLUÇÃO DA RECEITA REALIZADA NOS ÚLTIMOS DOZE MESES',
      alignment: 'center',
      bold: true,
      colSpan: 12
    },
      '', '', '', '', '', '', '', '', '', '', '',
    {
      text: 'Total 12 meses',
      rowSpan: 2, alignment: 'center', bold: true
    }],
    [
      { text: '' },
      { text: this.retornarNomeMes(-11), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-10), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-9), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-8), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-7), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-6), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-5), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-4), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-3), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-2), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(-1), alignment: 'center', bold: true },
      { text: this.retornarNomeMes(0), alignment: 'center', bold: true }
    ]];
    const grupos = this.funcaoService.agrupar(dados, 'tipo',
      ['soma0', 'soma1', 'soma2', 'soma3', 'soma4', 'soma5', 'soma6', 'soma7', 'soma8', 'soma9', 'soma10', 'soma11']);
    let somaTotal = 0.0;
    for (const grupo of grupos) {
      let somaGrupo = 0.0;
      for (let idx = 11; idx >= 0; idx--) {
        somaGrupo += +grupo.totalizadores[`soma${idx}`];
      }
      if (somaTotal === 0.0) {
        somaTotal += somaGrupo;
      } else {
        somaTotal -= somaGrupo;
      }
      registros.push([
        {
          text: `${grupo.grupo}`, bold: true
        },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma11`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma10`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma9`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma8`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma7`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma6`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma5`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma4`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma3`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma2`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma1`]), alignment: 'right', bold: true },
        { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma0`]), alignment: 'right', bold: true },
        {
          text: this.funcaoService.convertToBrNumber(somaGrupo), alignment: 'right', bold: true
        }
      ]);
      for (const registro of grupo.registros) {
        registros.push([
          {
            text: `${registro.nome}`
          },
          { text: this.funcaoService.convertToBrNumber(registro.soma11), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma10), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma9), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma8), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma7), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma6), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma5), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma4), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma3), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma2), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma1), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber(registro.soma0), alignment: 'right' },
          {
            text: this.funcaoService.convertToBrNumber((+registro.soma11 + +registro.soma10 + +registro.soma9 + +registro.soma8
              + +registro.soma7 + +registro.soma6 + +registro.soma5 + +registro.soma4
              + +registro.soma3 + +registro.soma2 + +registro.soma1 + +registro.soma0)), alignment: 'right'
          }
        ]);
      }
      registros.push([{
        text: ' ',
        colSpan: 14
      }, '', '', '', '', '', '', '', '', '', '', '', '', '']);
    }
    registros.push([{
      text: 'RECEITA CORRENTE LÍQUIDA', bold: true,
      alignment: 'center',
      colSpan: 13
    }, '', '', '', '', '', '', '', '', '', '', '', '', {
      text: this.funcaoService.convertToBrNumber(somaTotal),
      alignment: 'right', bold: true
    }]);
    this.valorRCL = somaTotal;
    return [{
      layout: 'linhas',
      table: {
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
        body: registros
      }
    }];
  }

  retornarNomeMes(diminuir: number) {
    const fator: number = +this.mes + +diminuir;
    if (fator > 0) {
      return ('00' + fator).slice(-2)
        + `/${this.exercicio.ano}`;
    } else {
      const meses: number = +12 + +fator;
      return ('00' + meses).slice(-2) + `/${+this.exercicio.ano - 1}`;
    }
  }

}
